import type { KbAdminEditorSectionProps } from "../kbAdminEditorProps";
import { KbAiDetailsClarifyingQuestions } from "./KbAiDetailsClarifyingQuestions";

type Props = Pick<
  KbAdminEditorSectionProps,
  | "aiGeneratedNotes"
  | "aiPlacementSummary"
  | "aiArticleLinkSuggestions"
  | "aiLinksOpen"
  | "previewRelatedArticle"
  | "aiResolvedFacts"
  | "aiRelatedArticles"
  | "relatedPreviewLoading"
  | "relatedPreview"
  | "setRelatedPreview"
  | "loadArticle"
  | "aiBodyDraft"
  | "setAiBodyDraft"
  | "aiBodyGenerationNotes"
  | "applyAiBodyDraft"
  | "preserveOriginalBody"
  | "restorePreviousBody"
  | "previousArticleContent"
  | "aiQuestionAnswers"
  | "updateAiQuestionAnswer"
  | "setAiQuestionDisposition"
  | "appliedSuggestion"
>;

export function KbAiDetailsResponseWindow(props: Props) {
  const {
    aiGeneratedNotes,
    aiPlacementSummary,
    aiArticleLinkSuggestions,
    aiLinksOpen,
    previewRelatedArticle,
    aiResolvedFacts,
    aiRelatedArticles,
    relatedPreviewLoading,
    relatedPreview,
    setRelatedPreview,
    loadArticle,
    aiBodyDraft,
    setAiBodyDraft,
    aiBodyGenerationNotes,
    applyAiBodyDraft,
    preserveOriginalBody,
    restorePreviousBody,
    previousArticleContent,
  } = props;

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-xs text-gray-700 space-y-3 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/45 dark:text-zinc-300">
      <div className="font-semibold text-gray-900 dark:text-zinc-100">AI response window</div>
      {aiGeneratedNotes.length ? (
        <div className="space-y-1">
          <div className="font-medium text-gray-900 dark:text-zinc-100">Why these settings</div>
          {aiGeneratedNotes.map((item) => (
            <div key={item}>{item}</div>
          ))}
        </div>
      ) : (
        <div className="text-gray-500 dark:text-zinc-400">Processing notes will appear here after you run AI processing.</div>
      )}

      {aiPlacementSummary ? (
        <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-zinc-700">
          <div className="font-medium text-gray-900 dark:text-zinc-100">Placement guidance</div>
          <div>{aiPlacementSummary}</div>
        </div>
      ) : null}

      {aiArticleLinkSuggestions.length && aiLinksOpen ? (
        <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-zinc-700">
          <div className="font-medium text-gray-900 dark:text-zinc-100">Article linking suggestions</div>
          {aiArticleLinkSuggestions.map((item) => (
            <div key={`${item.slug}-${item.relationship}`} className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-3 space-y-2 dark:border-sky-800 dark:bg-sky-950/35">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-gray-900 dark:text-zinc-100">{item.title}</div>
                  <div className="mt-1 text-[11px] text-gray-500 dark:text-zinc-400">{item.slug}</div>
                </div>
                <span className="rounded-full border border-sky-300 bg-white px-2 py-0.5 text-[11px] text-sky-900 dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-100">
                  {item.relationship.replaceAll("_", " ")}
                </span>
              </div>
              <div>{item.reason}</div>
              <div className="text-[11px] text-gray-500 dark:text-zinc-400">
                recommended action: {item.action === "revise" ? "review for update" : "link as supporting context"}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                  onClick={() => void previewRelatedArticle(item.slug, item.title)}
                >
                  Preview article
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {aiResolvedFacts.length ? (
        <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-zinc-700">
          <div className="font-medium text-gray-900 dark:text-zinc-100">Interview memory</div>
          {aiResolvedFacts.map((item) => (
            <div key={item}>{item}</div>
          ))}
        </div>
      ) : null}

      {aiRelatedArticles.length ? (
        <details className="pt-2 border-t border-gray-100 dark:border-zinc-700">
          <summary className="cursor-pointer list-none select-none">
            <div className="flex items-center justify-between gap-3">
              <div className="font-medium text-gray-900 dark:text-zinc-100">Related articles to review</div>
              <span className="text-[11px] text-gray-500 dark:text-zinc-400">
                {aiRelatedArticles.length} article{aiRelatedArticles.length === 1 ? "" : "s"} · click to expand
              </span>
            </div>
          </summary>
          <div className="mt-2 space-y-2">
            {aiRelatedArticles.map((item) => (
              <button
                key={`${item.slug}-${item.action}`}
                type="button"
                onClick={() => void previewRelatedArticle(item.slug, item.title)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-left hover:border-gray-300 hover:bg-white dark:border-zinc-700 dark:bg-zinc-900/50 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/80"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-zinc-100">{item.title}</div>
                    <div className="mt-1 text-[11px] text-gray-500 dark:text-zinc-400">{item.slug}</div>
                  </div>
                  <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-800 dark:border-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200">
                    {item.action === "revise" ? "review for edit" : "reference/link"}
                  </span>
                </div>
                <div className="mt-2">{item.reason}</div>
                <div className="mt-1 text-[11px] text-gray-500 dark:text-zinc-400">
                  scope {item.scope ?? "(none)"} • type {item.doc_type ?? "(none)"} • status {item.status} • edited by {item.last_edited_by ?? "unknown"} • click to preview safely
                </div>
              </button>
            ))}

            {relatedPreviewLoading ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-gray-500 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-400">Loading related article preview…</div>
            ) : null}

            {relatedPreview ? (
              <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-3 space-y-2 dark:border-sky-800 dark:bg-sky-950/35">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-zinc-100">{relatedPreview.title}</div>
                    <div className="mt-1 text-[11px] text-gray-500 dark:text-zinc-400">{relatedPreview.slug}</div>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                    onClick={() => setRelatedPreview(null)}
                  >
                    Close preview
                  </button>
                </div>
                <div className="text-[11px] text-gray-500 dark:text-zinc-400">
                  scope {relatedPreview.scope ?? "(none)"} • type {relatedPreview.doc_type ?? "(none)"} • status {relatedPreview.status} • edited by{" "}
                  {relatedPreview.last_edited_by ?? "unknown"}
                </div>
                <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-sky-100 bg-white px-3 py-2 text-[12px] text-gray-700 dark:border-sky-900/50 dark:bg-zinc-900/60 dark:text-zinc-200">
                  {relatedPreview.body_markdown.trim() || "No body content available for preview."}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                    onClick={() => void loadArticle(relatedPreview.slug, relatedPreview.title)}
                  >
                    Open in editor
                  </button>
                  <div className="self-center text-[11px] text-gray-500 dark:text-zinc-400">
                    Opening in editor replaces the current loaded document. Preview is non-destructive.
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </details>
      ) : null}

      {aiBodyDraft.trim() ? (
        <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-zinc-700">
          <div className="font-medium text-gray-900 dark:text-zinc-100">Generated body draft</div>
          {aiBodyGenerationNotes.length ? (
            <div className="space-y-1 text-gray-600 dark:text-zinc-400">
              {aiBodyGenerationNotes.map((item) => (
                <div key={item}>{item}</div>
              ))}
            </div>
          ) : null}
          <textarea
            className="h-48 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
            value={aiBodyDraft}
            onChange={(event) => setAiBodyDraft(event.target.value)}
            placeholder="AI body draft will appear here."
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white" onClick={() => applyAiBodyDraft("replace")}>
              {preserveOriginalBody ? "Apply structured draft to body" : "Replace body"}
            </button>
            <button type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200" onClick={() => applyAiBodyDraft("append")}>
              Append raw draft (advanced)
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
              onClick={restorePreviousBody}
              disabled={previousArticleContent == null}
            >
              Restore previous body
            </button>
          </div>
        </div>
      ) : null}

      <KbAiDetailsClarifyingQuestions {...props} />
    </div>
  );
}
